import React, { useRef, useState, useEffect, useCallback } from 'react';
import MarkdownIt from 'markdown-it';
import { X, RefreshCw, Download, Upload, HardDrive, Settings, FileCode, Key, Moon, Sun, Brain, ShieldCheck, User, Users, Crown, Clock, BookOpen, Loader2, BarChart2, Shield, RotateCcw, Sparkles, AlertTriangle, CheckCircle2, Cloud, ChevronRight, ChevronDown, Github, FilePlus, Trash2, FileEdit, GitMerge, Bot, Send, ExternalLink, Mic, MicOff, CreditCard, Check } from 'lucide-react';
import { AdminUserViewer } from './AdminUserViewer';
import { GitHubStatus } from '../services/githubService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Language, MindMapNode } from '../types';
import { saveUserPrefs } from '../services/storageService';
import { MathSymbolsSettings } from './MathSymbolsSettings';
import { SnapshotMeta, restoreSnapshot } from '../services/snapshotService';
import { sendDiffExplainMessage } from '../services/geminiService';

// ─── Snapshot diff utilities ──────────────────────────────────────────────────

// Shared markdown-it instance for semantic content normalization
const _md = new MarkdownIt();

type SDiffLine = { t: 'add' | 'del' | 'ctx'; s: string };

function lcsSDiff(a: string[], b: string[]): SDiffLine[] {
    const m = a.length, n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--)
        for (let j = n - 1; j >= 0; j--)
            dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    const out: SDiffLine[] = [];
    let i = 0, j = 0;
    while (i < m || j < n) {
        if (i < m && j < n && a[i] === b[j]) { out.push({ t: 'ctx', s: a[i] }); i++; j++; }
        else if (j < n && (i >= m || (dp[i + 1]?.[j] ?? 0) <= (dp[i]?.[j + 1] ?? 0))) { out.push({ t: 'add', s: b[j] }); j++; }
        else { out.push({ t: 'del', s: a[i] }); i++; }
    }
    return out;
}

function computeSnapLineDiff(oldText: string, newText: string): SDiffLine[] {
    const a = oldText.split('\n'), b = newText.split('\n');
    const m = a.length, n = b.length;
    let pre = 0;
    while (pre < m && pre < n && a[pre] === b[pre]) pre++;
    let suf = 0;
    while (suf < m - pre && suf < n - pre && a[m - 1 - suf] === b[n - 1 - suf]) suf++;
    const aMid = a.slice(pre, m - suf), bMid = b.slice(pre, n - suf);
    const mm = aMid.length, nn = bMid.length;
    let midDiff: SDiffLine[] = [];
    if (mm > 0 || nn > 0) {
        if (mm * nn <= 500000) midDiff = lcsSDiff(aMid, bMid);
        else midDiff = [...aMid.map(s => ({ t: 'del' as const, s })), ...bMid.map(s => ({ t: 'add' as const, s }))];
    }
    return [
        ...a.slice(0, pre).map(s => ({ t: 'ctx' as const, s })),
        ...midDiff,
        ...a.slice(m - suf).map(s => ({ t: 'ctx' as const, s })),
    ];
}

interface SnapNodeDiff {
    added: { id: string; title: string; createdAt?: number; titleSource?: string }[];
    deleted: { id: string; title: string; likelyReAdded: boolean; snapContent: string; currentContent: string }[];
    modified: { id: string; title: string; oldTitle: string; newTitle: string; titleChanged: boolean; oldContent: string; newContent: string; contentChanged: boolean; imageAdded: boolean; imageRemoved: boolean; imageChanged: boolean; snapRawTitle: string; snapRawContent: string; curRawContent: string; snapImageBase64: string; curImageBase64: string; curAllImages: string[] }[];
    unchangedCount: number;
}

// normalizeContent: parse both sides through markdown-it (the same parser TipTap uses)
// then extract plain text. This handles ALL markdown-it transformations systematically:
// entity decoding, backslash unescaping, bold/italic stripping, HR normalization, etc.
// Both sides go through the same pipeline → equal content always produces equal output.
const normalizeContent = (s: string): string =>
    _md.render(s)                          // parse markdown → HTML (handles all artifacts)
    .replace(/<[^>]+>/g, ' ')             // strip HTML tags → plain text
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')  // decode residual HTML entities in text nodes
    .replace(/\s+/g, ' ')                 // collapse all whitespace to single space
    .trim();

// normalizeForDisplay: lighter normalization that keeps **bold** markers for readable diffs.
// Uses regex to fix known TipTap artifacts — not exhaustive but good enough for display.
const normalizeForDisplay = (s: string): string =>
    s
    .replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&')
    .replace(/\\(.)/g, '$1')
    .replace(/^([ \t]*)([*+])[ \t]+/gm, '$1- ')
    .replace(/[^\S\n]+$/mg, '')
    .replace(/^([-*_])([ \t]*\1){2,}$/mg, '---')
    .replace(/([^\n]) *\n(?!\n)([^\n])/g, '$1 $2')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/([^\s=<>!])= /g, '$1 = ')
    .replace(/ =([^\s=])/g, ' = $1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

function computeSnapNodeDiff(currentNodes: MindMapNode[], snapNodes: MindMapNode[]): SnapNodeDiff {
    const snapMap = new Map(snapNodes.map(n => [n.id, n]));
    const curSet = new Set(currentNodes.map(n => n.id));
    // Build a map of current nodes by title for cross-checking manually re-created notes
    const currentByTitle = new Map<string, MindMapNode>();
    for (const n of currentNodes) {
        const key = n.title?.trim().toLowerCase();
        if (key) currentByTitle.set(key, n);
    }
    const added = currentNodes.filter(n => !snapMap.has(n.id)).map(n => ({ id: n.id, title: n.title || '', createdAt: n.createdAt, titleSource: n.titleSource }));
    const deleted = snapNodes.filter(n => !curSet.has(n.id)).map(n => {
        const titleKey = n.title?.trim().toLowerCase() ?? '';
        const matchedCurrent = titleKey ? currentByTitle.get(titleKey) : null;
        return {
            id: n.id,
            title: n.title || '',
            likelyReAdded: !!matchedCurrent,
            // Store both versions' content so UI can show a content diff
            snapContent: n.content ?? '',
            currentContent: matchedCurrent?.content ?? '',
        };
    });
    const modified: SnapNodeDiff['modified'] = [];
    let unchangedCount = 0;
    for (const cur of currentNodes) {
        const snap = snapMap.get(cur.id);
        if (!snap) continue;
        const titleChanged = (cur.title ?? '') !== (snap.title ?? '');
        const normalCur = normalizeContent(cur.content ?? '');
        const normalSnap = normalizeContent(snap.content ?? '');
        const contentChanged = normalCur !== normalSnap;
        const curAllImages: string[] = [];
        if (cur.imageBase64?.startsWith('https://')) curAllImages.push(cur.imageBase64);
        cur.attachments?.filter(a => a.type === 'image' && a.content).forEach(a => curAllImages.push(a.content));
        const curFirstImg = curAllImages[0] ?? '';
        const curHasImg = curAllImages.length > 0;
        const snapAllImages: string[] = [];
        if (snap.imageBase64?.startsWith('https://')) snapAllImages.push(snap.imageBase64);
        (snap as any).attachments?.filter((a: any) => a.type === 'image' && a.content).forEach((a: any) => snapAllImages.push(a.content));
        const snapFirstImg = snapAllImages[0] ?? '';
        const snapHasImg = snapAllImages.length > 0;
        const imageAdded = curHasImg && !snapHasImg;
        const imageRemoved = !curHasImg && snapHasImg;
        const imageChanged = curHasImg && snapHasImg && curFirstImg !== snapFirstImg;
        if (titleChanged || contentChanged || imageAdded || imageRemoved || imageChanged) {
            const fmt = (node: MindMapNode) => [node.title ? `# ${node.title}` : '', normalizeForDisplay(node.content ?? '')].filter(Boolean).join('\n\n');
            modified.push({ id: cur.id, title: cur.title || snap.title || '(Untitled)', oldTitle: snap.title ?? '', newTitle: cur.title ?? '', titleChanged, oldContent: fmt(snap), newContent: fmt(cur), contentChanged, imageAdded, imageRemoved, imageChanged, snapRawTitle: snap.title ?? '', snapRawContent: snap.content ?? '', curRawContent: cur.content ?? '', snapImageBase64: snapFirstImg, curImageBase64: curFirstImg, curAllImages });
        } else {
            unchangedCount++;
        }
    }
    return { added, deleted, modified, unchangedCount };
}

const SnapDiffViewer: React.FC<{ lines: SDiffLine[] }> = ({ lines }) => {
    const CTX = 2;
    const changeIdx = new Set<number>();
    lines.forEach((l, i) => { if (l.t !== 'ctx') changeIdx.add(i); });
    type VisLine = { key: number; line: SDiffLine; ellipsis?: true };
    const visible: VisLine[] = [];
    let lastWasEllipsis = false;
    lines.forEach((line, i) => {
        const near = [...changeIdx].some(ci => Math.abs(i - ci) <= CTX);
        if (line.t !== 'ctx' || near) {
            visible.push({ key: i, line });
            lastWasEllipsis = false;
        } else if (!lastWasEllipsis) {
            visible.push({ key: i, line, ellipsis: true });
            lastWasEllipsis = true;
        }
    });
    return (
        <div className="font-mono text-[10px] leading-[17px] max-h-48 overflow-y-auto bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
            {visible.map(({ key, line, ellipsis }) => ellipsis ? (
                <div key={key} className="px-3 py-0.5 text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-slate-800/50 text-center select-none">···</div>
            ) : (
                <div key={key} className={`flex gap-1.5 px-3 py-px ${line.t === 'add' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : line.t === 'del' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300' : 'text-gray-400 dark:text-gray-600'}`}>
                    <span className="select-none w-3 shrink-0 text-center">{line.t === 'add' ? '+' : line.t === 'del' ? '−' : ' '}</span>
                    <span className="whitespace-pre-wrap break-all">{line.s || ' '}</span>
                </div>
            ))}
        </div>
    );
};

type TabType = 'general' | 'math' | 'premium' | 'data' | 'admin';

const TabButton: React.FC<{
  tab: TabType;
  icon: React.ReactNode;
  label: string;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}> = React.memo(({ tab, icon, label, activeTab, onTabChange }) => (
  <button
    onClick={() => onTabChange(tab)}
    className={`shrink-0 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${activeTab === tab
      ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
));

const IndexAllButton: React.FC<{ onIndexAllNotes: () => Promise<void>; language?: Language }> = ({ onIndexAllNotes, language }) => {
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const handleClick = async () => {
    setStatus('running');
    try {
      await onIndexAllNotes();
      setStatus('done');
      setTimeout(() => setStatus('idle'), 4000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={status === 'running'}
      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-60 transition-colors"
    >
      {status === 'running' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {status === 'idle' && (language === 'vietnamese' ? 'Index tất cả note' : 'Index all notes')}
      {status === 'running' && (language === 'vietnamese' ? 'Đang index...' : 'Indexing...')}
      {status === 'done' && (language === 'vietnamese' ? 'Hoàn tất!' : 'Done!')}
      {status === 'error' && (language === 'vietnamese' ? 'Lỗi, thử lại' : 'Error, retry')}
    </button>
  );
};

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (apiKey: string, clientId: string) => void;
  initialApiKey: string;
  initialClientId: string;
  language?: Language;
  onExportBackup?: () => void;
  onImportBackup?: (file: File) => void;
  onExportSmmx?: () => void;
  onImportSmmx?: (file: File) => void;
  isPremium?: boolean;
  isAdmin?: boolean;
  simulateFreeUser?: boolean;
  onSimulateFreeUserChange?: (v: boolean) => void;
  initialTab?: 'general' | 'math' | 'premium' | 'data';
  notebooks?: { id: string; title: string; nodeCount?: number; imageCount?: number }[];
  onGetCurrentNodes?: (notebookId: string) => Promise<MindMapNode[] | null>;
  onRestoreMissingScreenshots?: (notebookId: string, snapshotId: string) => Promise<{ restored: number }>;
  onRestoreMissingNotes?: (notebookId: string, snapshotId: string, nodeIds?: string[]) => Promise<{ restored: number }>;
  activeNotebookId?: string | null;
  userId?: string;
  onManualBackup?: (notebookId: string) => Promise<boolean>;
  onListSnapshots?: (notebookId: string) => Promise<SnapshotMeta[]>;
  onRestoreSnapshot?: (notebookId: string, snapshotId: string) => Promise<void>;
  dataTabInitialNotebookId?: string;
  autoTitleEnabled?: boolean;
  onAutoTitleEnabledChange?: (v: boolean) => void;
  bgAutoTitleStatus?: string | null;
  autoTitlePendingCount?: number;
  autoTitleStuckCount?: number;
  onResetStuckAutoTitle?: () => void;
  driveConnected?: boolean;
  onDriveConnect?: () => void;
  onDriveDisconnect?: () => void;
  onDriveBackup?: (notebookId: string) => Promise<string>;
  onDriveRestore?: (fileId: string) => Promise<void>;
  driveBackupRecords?: { id: string; name: string; modifiedTime: string; size: string }[];
  onIndexAllNotes?: () => Promise<void>;
  onRevertNodeChanges?: (notebookId: string, nodeId: string, changes: { title?: string; content?: string; imageBase64?: string }) => Promise<void>;
  onOpenNote?: (notebookId: string, nodeId: string) => void;
  githubStatus?: GitHubStatus | null;
  onConnectGitHub?: () => void;
  onUpdateGitHubRepo?: (repo: string, branch: string) => Promise<void>;
  onClearGitHubConfig?: () => Promise<void>;
  onUpgrade?: () => void;
  onManageSubscription?: () => void;
  subscriptionStatus?: string;
  isMobile?: boolean;
  onMobileSync?: () => Promise<void>;
  hasUnsyncedChanges?: boolean;
  syncState?: string;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen, onClose, language = 'english',
  onExportBackup, onImportBackup, onExportSmmx, onImportSmmx, isPremium = false,
  isAdmin = false, simulateFreeUser = false, onSimulateFreeUserChange,
  initialTab = 'general',
  notebooks, activeNotebookId,
  userId, onManualBackup, onListSnapshots, onRestoreSnapshot, onGetCurrentNodes,
  onRestoreMissingScreenshots, onRestoreMissingNotes, dataTabInitialNotebookId,
  autoTitleEnabled = false, onAutoTitleEnabledChange,
  bgAutoTitleStatus, autoTitlePendingCount = 0, autoTitleStuckCount = 0, onResetStuckAutoTitle,
  driveConnected = false, onDriveConnect, onDriveDisconnect, onDriveBackup, onDriveRestore, driveBackupRecords = [],
  onIndexAllNotes,
  onRevertNodeChanges,
  onOpenNote,
  githubStatus, onConnectGitHub, onUpdateGitHubRepo, onClearGitHubConfig,
  onUpgrade, onManageSubscription, subscriptionStatus,
  isMobile = false, onMobileSync, hasUnsyncedChanges = false, syncState,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const smmxInputRef = useRef<HTMLInputElement>(null);

  const [driveBackupStatus, setDriveBackupStatus] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [driveBackupUrls, setDriveBackupUrls] = useState<Record<string, string>>({});
  const [driveRestoreStatus, setDriveRestoreStatus] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});

  const [ghRepo, setGhRepo] = useState(githubStatus?.repo || '');
  const [ghBranch, setGhBranch] = useState(githubStatus?.branch || 'main');
  const [ghSaving, setGhSaving] = useState(false);

  const handleDriveBackup = async (notebookId: string) => {
    if (!onDriveBackup) return;
    setDriveBackupStatus(prev => ({ ...prev, [notebookId]: 'loading' }));
    try {
      const url = await onDriveBackup(notebookId);
      setDriveBackupUrls(prev => ({ ...prev, [notebookId]: url }));
      setDriveBackupStatus(prev => ({ ...prev, [notebookId]: 'success' }));
      setTimeout(() => setDriveBackupStatus(prev => ({ ...prev, [notebookId]: 'idle' })), 8000);
    } catch {
      setDriveBackupStatus(prev => ({ ...prev, [notebookId]: 'error' }));
      setTimeout(() => setDriveBackupStatus(prev => ({ ...prev, [notebookId]: 'idle' })), 4000);
    }
  };

  const handleDriveRestore = async (fileId: string) => {
    if (!onDriveRestore) return;
    setDriveRestoreStatus(prev => ({ ...prev, [fileId]: 'loading' }));
    try {
      await onDriveRestore(fileId);
      setDriveRestoreStatus(prev => ({ ...prev, [fileId]: 'success' }));
      setTimeout(() => setDriveRestoreStatus(prev => ({ ...prev, [fileId]: 'idle' })), 3000);
    } catch {
      setDriveRestoreStatus(prev => ({ ...prev, [fileId]: 'error' }));
      setTimeout(() => setDriveRestoreStatus(prev => ({ ...prev, [fileId]: 'idle' })), 4000);
    }
  };

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Sync tab when initialTab changes or modal opens
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Safety snapshots — must be declared BEFORE effects that reference them
  const [snapshotNotebookId, setSnapshotNotebookId] = useState<string>('');
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotBacking, setSnapshotBacking] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Diff state for snapshot comparison
  const [diffSnapshotId, setDiffSnapshotId] = useState<string | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffResult, setDiffResult] = useState<SnapNodeDiff | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffKey, setDiffKey] = useState(0);
  const [diffExpandedId, setDiffExpandedId] = useState<string | null>(null);
  const [selectiveRestoring, setSelectiveRestoring] = useState<'screenshots' | 'notes' | null>(null);
  const [selectiveRestoreMsg, setSelectiveRestoreMsg] = useState<string | null>(null);

  // AI chat for diff explanation
  type SnapAiMsg = { role: 'user' | 'model'; text: string };
  const [snapAiChat, setSnapAiChat] = useState<SnapAiMsg[]>([]);
  const [snapAiInput, setSnapAiInput] = useState('');
  const [snapAiLoading, setSnapAiLoading] = useState(false);
  const [showSnapAiChat, setShowSnapAiChat] = useState(false);
  const [aiPanelWidth, setAiPanelWidth] = useState(340);
  const snapAiScrollRef = useRef<HTMLDivElement>(null);
  const aiDragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [snapAiMicActive, setSnapAiMicActive] = useState(false);
  const snapAiRecognitionRef = useRef<InstanceType<typeof window.SpeechRecognition> | null>(null);

  // Keep a stable ref for notebooks to avoid running this effect on every parent render
  const notebooksRef = useRef(notebooks);
  notebooksRef.current = notebooks;
  const onGetCurrentNodesRef = useRef(onGetCurrentNodes);
  onGetCurrentNodesRef.current = onGetCurrentNodes;

  // Always sync to the active notebook on canvas (no user selection allowed).
  // dataTabInitialNotebookId overrides when triggered from context menu on a specific notebook.
  useEffect(() => {
    if (dataTabInitialNotebookId) setSnapshotNotebookId(dataTabInitialNotebookId);
    else if (activeNotebookId) setSnapshotNotebookId(activeNotebookId);
    else if (notebooksRef.current && notebooksRef.current.length > 0) setSnapshotNotebookId(notebooksRef.current[0].id);
  }, [dataTabInitialNotebookId, activeNotebookId]);

  // Load snapshots when data tab is open and a notebook is selected
  useEffect(() => {
    if (!isOpen || activeTab !== 'data' || !snapshotNotebookId || !onListSnapshots) return;
    setSnapshotsLoading(true);
    onListSnapshots(snapshotNotebookId)
      .then(setSnapshots)
      .catch(() => setSnapshots([]))
      .finally(() => setSnapshotsLoading(false));
  }, [isOpen, activeTab, snapshotNotebookId, onListSnapshots]);

  // Reset diff when user switches notebook
  useEffect(() => {
    setDiffSnapshotId(null);
    setDiffResult(null);
    setDiffError(null);
    setDiffExpandedId(null);
    setSelectiveRestoreMsg(null);
    setSnapAiChat([]);
    setShowSnapAiChat(false);
  }, [snapshotNotebookId]);

  // Load diff when a snapshot is selected for comparison
  useEffect(() => {
    if (!diffSnapshotId || !snapshotNotebookId || !userId) { setDiffResult(null); setDiffError(null); return; }
    setDiffLoading(true);
    setDiffResult(null);
    setDiffError(null);
    setDiffExpandedId(null);
    setSnapAiChat([]);
    setShowSnapAiChat(false);
    const vi = language === 'vietnamese';
    (async () => {
      try {
        const [{ nodes: snapNodes }, curNodes] = await Promise.all([
          restoreSnapshot(userId, snapshotNotebookId, diffSnapshotId),
          onGetCurrentNodesRef.current ? onGetCurrentNodesRef.current(snapshotNotebookId) : Promise.resolve([] as MindMapNode[]),
        ]);
        // null means load failed — do NOT treat as empty notebook (would show all notes as "deleted")
        if (curNodes === null) {
          setDiffError(vi ? 'Không thể tải ghi chú hiện tại. Vui lòng thử lại.' : 'Could not load current notebook data. Please retry.');
          return;
        }
        setDiffResult(computeSnapNodeDiff(curNodes, snapNodes));
      } catch {
        setDiffError(vi ? 'Lỗi tải diff.' : 'Error loading diff.');
      } finally {
        setDiffLoading(false);
      }
    })();
  }, [diffSnapshotId, snapshotNotebookId, userId, diffKey, language]);

  // M4: Premium Code State (Correct Placement - BEFORE early return)
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Admin stats
  type AdminUser = { uid: string; email: string; name: string; photoURL: string | null; lastSeen: number; isPremium: boolean; notebookCount: number; todayUsage: number };
  type AdminStats = { users: AdminUser[]; total: number; premiumCount: number; activeToday: number };
  type AdminNotebookData = { id: string; title: string; createdAt: number; updatedAt: number; isDeleted: boolean; pdfContextUrl: string | null; nodes: any[] };
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [viewingUser, setViewingUser] = useState<AdminUser | null>(null);
  const [viewingUserNotebooks, setViewingUserNotebooks] = useState<AdminNotebookData[]>([]);
  const [viewingUserLoading, setViewingUserLoading] = useState(false);

  const fetchAdminStats = useCallback(async () => {
    setAdminLoading(true);
    setAdminError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'getAdminStats');
      const result = await fn({});
      setAdminStats(result.data as AdminStats);
    } catch (e: any) {
      setAdminError(e.message || 'Failed to load stats');
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const fetchUserData = useCallback(async (user: AdminUser) => {
    setViewingUserLoading(true);
    setViewingUser(user);
    setViewingUserNotebooks([]);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'getAdminUserData');
      const result = await fn({ uid: user.uid });
      setViewingUserNotebooks((result.data as any).notebooks || []);
    } catch (e: any) {
      setViewingUserNotebooks([]);
    } finally {
      setViewingUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === 'admin' && isAdmin && !adminStats) {
      fetchAdminStats();
    }
  }, [isOpen, activeTab, isAdmin, adminStats, fetchAdminStats]);

  // Dark mode toggle
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() =>
    document.documentElement.classList.contains('dark')
  );

  const toggleDarkMode = () => {
    const newVal = !isDarkMode;
    setIsDarkMode(newVal);
    document.documentElement.classList.toggle('dark', newVal);
    localStorage.setItem('darkMode', newVal ? 'true' : 'false');
    if (userId && userId !== 'guest') saveUserPrefs(userId, { darkMode: newVal });
  };

  const handleActivate = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const fn = httpsCallable(getFunctions(), 'redeemInviteCode');
      await fn({ code: code.trim() });
      alert(language === 'vietnamese' ? '✅ Kích hoạt thành công! Vui lòng tải lại trang.' : '✅ Activated successfully! Please refresh the page.');
      setCode("");
    } catch (e: any) {
      alert('❌ ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImportBackup) {
      onImportBackup(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSmmxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImportSmmx) {
      onImportSmmx(file);
    }
    if (smmxInputRef.current) smmxInputRef.current.value = '';
  };

  const handleAiPanelDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    aiDragRef.current = { startX: e.clientX, startWidth: aiPanelWidth };
    const onMove = (ev: MouseEvent) => {
      if (!aiDragRef.current) return;
      const delta = aiDragRef.current.startX - ev.clientX;
      setAiPanelWidth(Math.max(240, Math.min(600, aiDragRef.current.startWidth + delta)));
    };
    const onUp = () => {
      aiDragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [aiPanelWidth]);

  const sendSnapAiMessage = useCallback(async (text: string) => {
    if (!text.trim() || snapAiLoading || !diffResult) return;
    const msg = text.trim();
    setSnapAiInput('');
    setShowSnapAiChat(true);
    const history = [...snapAiChat, { role: 'user' as const, text: msg }];
    setSnapAiChat(history);
    setSnapAiLoading(true);
    setTimeout(() => snapAiScrollRef.current?.scrollTo({ top: snapAiScrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
    const vi = language === 'vietnamese';
    try {
      const trulyDeleted = diffResult.deleted.filter(n => !n.likelyReAdded);
      const summary = {
        newFiles: diffResult.added.length,
        modifiedFiles: diffResult.modified.length,
        deletedFiles: trulyDeleted.length,
        unchangedFiles: diffResult.unchangedCount,
        changedFileTitles: [
          ...diffResult.added.map(n => n.title || '(Untitled)'),
          ...trulyDeleted.map(n => n.title || '(Untitled)'),
          ...diffResult.modified.map(n => n.title || '(Untitled)'),
        ].slice(0, 20),
      };
      const sampleLines: string[] = [];
      for (const m of diffResult.modified.slice(0, 5)) {
        if (m.contentChanged && m.oldContent && m.newContent) {
          sampleLines.push(`[${m.title}]`);
          const lines = m.newContent.split('\n').slice(0, 5);
          lines.forEach(l => sampleLines.push(`+ ${l}`));
        }
      }
      const reply = await sendDiffExplainMessage(
        snapAiChat.map(m => ({ role: m.role, text: m.text })),
        msg,
        notebooks?.find(n => n.id === snapshotNotebookId)?.title ?? snapshotNotebookId,
        summary,
        sampleLines,
        vi ? 'vietnamese' : 'english'
      );
      setSnapAiChat(prev => [...prev, { role: 'model', text: reply }]);
    } catch (e: any) {
      const vi2 = language === 'vietnamese';
      setSnapAiChat(prev => [...prev, { role: 'model', text: vi2 ? `Lỗi: ${e.message}` : `Error: ${e.message}` }]);
    } finally {
      setSnapAiLoading(false);
      setTimeout(() => snapAiScrollRef.current?.scrollTo({ top: snapAiScrollRef.current.scrollHeight, behavior: 'smooth' }), 100);
    }
  }, [snapAiChat, snapAiLoading, diffResult, language, notebooks, snapshotNotebookId]);

  const toggleSnapAiMic = useCallback(() => {
    if (snapAiMicActive) {
      snapAiRecognitionRef.current?.stop();
      setSnapAiMicActive(false);
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Speech recognition is not supported in this browser.'); return; }
    const recognition = new SR();
    recognition.lang = language === 'vietnamese' ? 'vi-VN' : 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    snapAiRecognitionRef.current = recognition;
    setSnapAiMicActive(true);
    let finalTranscript = '';
    recognition.onresult = (e: any) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setSnapAiInput(finalTranscript + interim);
    };
    recognition.onend = () => {
      setSnapAiMicActive(false);
      snapAiRecognitionRef.current = null;
    };
    recognition.onerror = () => { setSnapAiMicActive(false); snapAiRecognitionRef.current = null; };
    recognition.start();
  }, [snapAiMicActive, language, sendSnapAiMessage]);

  if (!isOpen) return null;

  const labels = {
    english: {
      title: "Settings",
      backupTitle: "Data & Offline Backup",
      backupDesc: "Download all your notes and images as a professional .zip file for safe keeping.",
      exportBtn: "Export Backup (.zip)",
      importBtn: "Restore from Backup",
      exportSmmxBtn: "Export as SimpleMind (.smmx)",
      importSmmxBtn: "Import SimpleMind (.smmx)",
      close: "Close",
      reload: "Reload App",
      premiumCode: "Premium Code",
      activate: "Activate",
      appearance: "Appearance",
      darkMode: "Dark Mode",
      lightMode: "Light Mode",
      tabGeneral: "General",
      tabMath: "Math",
      tabPremium: "Premium",
      tabData: "Data"
    },
    vietnamese: {
      title: "Cài đặt",
      backupTitle: "Dữ liệu & Sao lưu ngoại tuyến",
      backupDesc: "Tải toàn bộ ghi chú và hình ảnh về máy dưới dạng file .zip để lưu trữ an toàn.",
      exportBtn: "Xuất bản sao lưu (.zip)",
      importBtn: "Phục hồi từ bản sao lưu",
      exportSmmxBtn: "Xuất sang SimpleMind (.smmx)",
      importSmmxBtn: "Nhập SimpleMind (.smmx)",
      close: "Đóng",
      reload: "Tải lại App",
      premiumCode: "Mã Cao Cấp",
      activate: "Kích hoạt",
      appearance: "Giao diện",
      darkMode: "Chế độ tối",
      lightMode: "Chế độ sáng",
      tabGeneral: "Tổng quát",
      tabMath: "Toán học",
      tabPremium: "Cao cấp",
      tabData: "Dữ liệu"
    }
  };

  const t = labels[language] || labels.english;

  return (
    <>
    <div className={`fixed z-[10001] flex ${isMobile ? 'flex-col inset-x-0 bottom-0 top-16' : 'inset-0 bg-black/60 backdrop-blur-sm items-center justify-center p-4'}`}>
      <div className={`bg-white dark:bg-slate-800 flex flex-col overflow-hidden ${isMobile ? 'flex-1' : 'rounded-2xl w-full max-w-2xl max-h-[90vh] shadow-2xl border dark:border-slate-700 animate-in zoom-in-95 duration-200'}`}>

        {/* Header */}
        <div className="p-5 border-b dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg">
              <Settings size={20} />
            </div>
            <h3 className="font-bold text-lg dark:text-white">{t.title}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors text-gray-500">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          <TabButton tab="general" icon={<Moon size={16} />} label={t.tabGeneral} activeTab={activeTab} onTabChange={setActiveTab} />
          <TabButton tab="math" icon={<span className="text-lg">∑</span>} label={t.tabMath} activeTab={activeTab} onTabChange={setActiveTab} />
          <TabButton tab="premium" icon={<Key size={16} />} label={t.tabPremium} activeTab={activeTab} onTabChange={setActiveTab} />
          <TabButton tab="data" icon={<HardDrive size={16} />} label={t.tabData} activeTab={activeTab} onTabChange={setActiveTab} />
          {isAdmin && <TabButton tab="admin" icon={<ShieldCheck size={16} />} label="Admin" activeTab={activeTab} onTabChange={setActiveTab} />}
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Mobile sync button — only shown on mobile */}
              {isMobile && onMobileSync && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                    <Cloud size={18} className="text-blue-500" />
                    <span>{language === 'vietnamese' ? 'Đồng bộ' : 'Sync'}</span>
                  </div>
                  <button
                    onClick={onMobileSync}
                    disabled={syncState === 'saving'}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${hasUnsyncedChanges && syncState !== 'saving' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'bg-gray-50 dark:bg-slate-800/60 border-gray-100 dark:border-slate-700 opacity-60'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${hasUnsyncedChanges && syncState !== 'saving' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-slate-700'}`}>
                        {syncState === 'saving' ? <Loader2 size={18} className="animate-spin text-blue-500" /> : <Cloud size={18} className={hasUnsyncedChanges ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />}
                      </div>
                      <div className="text-left">
                        <span className="font-semibold text-gray-800 dark:text-gray-100 block">
                          {language === 'vietnamese' ? 'Đồng bộ thủ công' : 'Manual Sync'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {syncState === 'saving'
                            ? (language === 'vietnamese' ? 'Đang đồng bộ...' : 'Syncing...')
                            : hasUnsyncedChanges
                              ? (language === 'vietnamese' ? 'Có thay đổi chưa được lưu lên cloud' : 'You have unsynced changes')
                              : (language === 'vietnamese' ? 'Dữ liệu đã đồng bộ' : 'Data is up to date')}
                        </span>
                      </div>
                    </div>
                    {hasUnsyncedChanges && syncState !== 'saving' && <RefreshCw size={18} className="text-blue-500 flex-shrink-0" />}
                  </button>
                </div>
              )}
              {/* Auto Title */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <Sparkles size={18} className="text-amber-500" />
                  <span>{language === 'vietnamese' ? 'Tự động' : 'Automation'}</span>
                </div>
                <button
                  onClick={() => { onAutoTitleEnabledChange?.(!autoTitleEnabled); }}
                  className="w-full flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 rounded-xl transition-all hover:bg-amber-100 dark:hover:bg-amber-900/30"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-full">
                      <Sparkles size={18} className="text-amber-600 dark:text-amber-300" />
                    </div>
                    <div className="text-left">
                      <span className="font-semibold text-gray-800 dark:text-gray-100 block">
                        {language === 'vietnamese' ? 'Tự động tạo tiêu đề' : 'Auto-generate titles'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {language === 'vietnamese'
                          ? 'Âm thầm tạo tiêu đề cho ghi chú chưa có tên khi mở notebook (tối đa 10/lần, 30/ngày)'
                          : 'Silently generate titles for untitled notes when opening a notebook (max 10/session, 30/day)'}
                      </span>
                    </div>
                  </div>
                  <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${autoTitleEnabled ? 'bg-amber-500' : 'bg-gray-300'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${autoTitleEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </button>

                {/* Auto-title status card */}
                {autoTitleEnabled && (() => {
                  const today = new Date().toISOString().split('T')[0];
                  const dailyCount = parseInt(localStorage.getItem(`autoTitle_daily_${today}`) || '0');
                  const MAX_PER_DAY = 30;
                  const limitReached = dailyCount >= MAX_PER_DAY;
                  return (
                    <div className="mt-2 px-3 py-2.5 bg-white dark:bg-slate-800/60 border border-amber-100 dark:border-amber-900/30 rounded-xl text-xs space-y-1.5">
                      {/* Running status */}
                      {bgAutoTitleStatus ? (
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <Loader2 size={11} className="animate-spin shrink-0" />
                          <span>{bgAutoTitleStatus}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-slate-500">
                          <CheckCircle2 size={11} className="shrink-0" />
                          <span>{language === 'vietnamese' ? 'Không có tác vụ nào đang chạy' : 'No task running'}</span>
                        </div>
                      )}

                      {/* Daily count bar */}
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-slate-400 shrink-0">
                          {language === 'vietnamese' ? 'Hôm nay:' : 'Today:'}
                        </span>
                        <div className="flex-1 h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${limitReached ? 'bg-red-400' : 'bg-amber-400'}`}
                            style={{ width: `${Math.min(100, (dailyCount / MAX_PER_DAY) * 100)}%` }}
                          />
                        </div>
                        <span className={`shrink-0 font-mono font-semibold ${limitReached ? 'text-red-500' : 'text-gray-600 dark:text-slate-300'}`}>
                          {dailyCount}/{MAX_PER_DAY}
                        </span>
                        {limitReached && (
                          <AlertTriangle size={11} className="text-red-500 shrink-0" />
                        )}
                      </div>

                      {/* Pending + stuck */}
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500 dark:text-slate-400">
                          {language === 'vietnamese'
                            ? `Chờ xử lý: ${autoTitlePendingCount} ghi chú`
                            : `Pending: ${autoTitlePendingCount} notes`}
                          {autoTitleStuckCount > 0 && (
                            <span className="ml-1 text-orange-500">
                              · {autoTitleStuckCount} {language === 'vietnamese' ? 'bị kẹt' : 'stuck'}
                            </span>
                          )}
                        </span>
                        {autoTitleStuckCount > 0 && onResetStuckAutoTitle && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onResetStuckAutoTitle(); }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 border border-orange-200 dark:border-orange-800 transition-colors"
                          >
                            <RotateCcw size={9} />
                            {language === 'vietnamese' ? 'Reset' : 'Retry'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <Moon size={18} className="text-purple-500" />
                  <span>{t.appearance}</span>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border-2 transition-all ${isDarkMode ? 'bg-purple-100 border-purple-300 dark:bg-purple-900/40 dark:border-purple-700 text-purple-700 dark:text-purple-300' : 'border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                >
                  <span className="text-sm font-semibold">{t.darkMode}</span>
                  {isDarkMode ? <Moon size={18} className="text-purple-500" /> : <Sun size={18} className="text-yellow-500" />}
                </button>
              </div>
            </div>
          )}

          {/* MATH TAB */}
          {activeTab === 'math' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <span className="text-xl">∑</span>
                  <span>{language === 'vietnamese' ? 'Ký hiệu Toán học' : 'Math Symbols & Patterns'}</span>
                </div>
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-900/30 rounded-xl">
                  <MathSymbolsSettings />
                </div>
              </div>
            </div>
          )}

          {/* PREMIUM TAB */}
          {activeTab === 'premium' && (
            <div className="space-y-6 animate-in fade-in duration-200">

              {/* BILLING SECTION */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <CreditCard size={18} className="text-indigo-500" />
                  <span>{language === 'vietnamese' ? 'Gói đăng ký & Thanh toán' : 'Subscription & Billing'}</span>
                </div>

                {isPremium ? (
                  <div className="space-y-2">
                    {/* Status card */}
                    <div className={`p-4 rounded-xl border flex items-start gap-3 ${
                      subscriptionStatus === 'past_due'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    }`}>
                      <div className={`p-1.5 rounded-full mt-0.5 ${subscriptionStatus === 'past_due' ? 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300' : 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'}`}>
                        <Check size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-bold text-sm ${subscriptionStatus === 'past_due' ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}`}>
                          {subscriptionStatus === 'past_due'
                            ? (language === 'vietnamese' ? 'Thanh toán thất bại' : 'Payment failed')
                            : (language === 'vietnamese' ? 'Premium đang hoạt động ✦' : 'Premium active ✦')}
                        </h4>
                        <p className={`text-xs mt-0.5 ${subscriptionStatus === 'past_due' ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                          {subscriptionStatus === 'past_due'
                            ? (language === 'vietnamese' ? 'Vui lòng cập nhật phương thức thanh toán.' : 'Please update your payment method below.')
                            : subscriptionStatus === 'canceled'
                            ? (language === 'vietnamese' ? 'Đã hủy — còn hiệu lực đến hết kỳ.' : 'Canceled — active until end of period.')
                            : (language === 'vietnamese' ? 'Toàn bộ tính năng AI đã được mở khóa.' : 'All AI features unlocked.')}
                        </p>
                      </div>
                    </div>

                    {/* Action buttons */}
                    {onManageSubscription && (
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={onManageSubscription}
                          className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          <CreditCard size={15} />
                          {language === 'vietnamese' ? 'Quản lý thanh toán & thẻ' : 'Manage billing & payment method'}
                        </button>
                        <p className="text-[11px] text-center text-gray-400 dark:text-gray-500">
                          {language === 'vietnamese'
                            ? 'Cập nhật thẻ, hủy gói, xem lịch sử hóa đơn — qua Stripe Portal'
                            : 'Update card, cancel plan, view invoices — via Stripe Portal'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Upgrade CTA */}
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/40 dark:to-blue-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-indigo-900 dark:text-indigo-200 text-sm">StudyBoard Premium</h4>
                          <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-0.5">
                            {language === 'vietnamese' ? 'AI không giới hạn · Sổ tay không giới hạn' : 'Unlimited AI · Unlimited notebooks'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black text-indigo-900 dark:text-indigo-200">$4.99</p>
                          <p className="text-[10px] text-indigo-500">/month</p>
                        </div>
                      </div>
                      {onUpgrade && (
                        <button
                          onClick={onUpgrade}
                          className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-sm transition-colors"
                        >
                          {language === 'vietnamese' ? 'Nâng cấp ngay →' : 'Upgrade now →'}
                        </button>
                      )}
                    </div>

                    {/* Invite code */}
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {language === 'vietnamese' ? 'Có mã mời?' : 'Have an invite code?'}
                      </p>
                      <div className="flex gap-2">
                        <input
                          value={code}
                          onChange={e => setCode(e.target.value)}
                          placeholder={language === 'vietnamese' ? 'Nhập mã mời...' : 'Enter invite code...'}
                          className="flex-1 px-4 py-2 border rounded-xl text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                        />
                        <button
                          onClick={handleActivate}
                          disabled={!code || loading}
                          className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-bold disabled:opacity-50 transition-colors"
                        >
                          {loading ? "..." : t.activate}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* AI KEYS */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <Brain size={18} className="text-indigo-500" />
                  <span>{language === 'vietnamese' ? 'API Key cá nhân (BYOK)' : 'Personal API Keys (BYOK)'}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {language === 'vietnamese'
                    ? 'Dùng model mạnh nhất (Gemini 2.5 Pro, GPT-4o) cho chat AI trong note. Key chỉ lưu trên máy bạn, không bao giờ lên server.'
                    : 'Use the most powerful models (Gemini 2.5 Pro, GPT-4o) for in-note AI chat. Key is stored locally only, never sent to our servers.'}
                </p>
                <div className="space-y-2">
                  {/* Gemini Key */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Google Gemini API Key</label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        defaultValue={typeof window !== 'undefined' ? localStorage.getItem('sb_user_gemini_key') ?? '' : ''}
                        onChange={e => { if (e.target.value) localStorage.setItem('sb_user_gemini_key', e.target.value); else localStorage.removeItem('sb_user_gemini_key'); }}
                        placeholder="AIza..."
                        className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
                      />
                      <button onClick={() => { localStorage.removeItem('sb_user_gemini_key'); window.location.reload(); }} className="px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors border border-red-200 dark:border-red-800">
                        {language === 'vietnamese' ? 'Xóa' : 'Clear'}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400">{language === 'vietnamese' ? 'Lấy tại' : 'Get at'} <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline">aistudio.google.com/apikey</a></p>
                  </div>
                  {/* OpenAI Key */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">OpenAI API Key</label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        defaultValue={typeof window !== 'undefined' ? localStorage.getItem('sb_user_openai_key') ?? '' : ''}
                        onChange={e => { if (e.target.value) localStorage.setItem('sb_user_openai_key', e.target.value); else localStorage.removeItem('sb_user_openai_key'); }}
                        placeholder="sk-..."
                        className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
                      />
                      <button onClick={() => { localStorage.removeItem('sb_user_openai_key'); window.location.reload(); }} className="px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors border border-red-200 dark:border-red-800">
                        {language === 'vietnamese' ? 'Xóa' : 'Clear'}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400">{language === 'vietnamese' ? 'Lấy tại' : 'Get at'} <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline">platform.openai.com/api-keys</a></p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DATA TAB */}
          {activeTab === 'data' && (
            <div className="space-y-6 animate-in fade-in duration-200">

              {/* SAFETY SNAPSHOTS */}
              {onListSnapshots && notebooks && notebooks.length > 0 && (() => {
                const vi = language === 'vietnamese';
                const curNb = notebooks.find(n => n.id === snapshotNotebookId);
                const currentNodeCount = curNb?.nodeCount ?? 0;
                // imageCount is undefined when notebook is evicted — treat as unknown, not 0
                const imageCountKnown = curNb?.imageCount !== undefined;
                const currentImageCount = curNb?.imageCount ?? 0;
                const latestSnap = snapshots[0] ?? null;
                // Use the best recent snapshot (by nodes + images) for the warning threshold,
                // not just the latest — because auto-backup may have captured a post-corruption state.
                const bestRecentSnap = !snapshotsLoading ? snapshots.slice(0, 20).reduce<SnapshotMeta | null>((best, s) => {
                  if (!best) return s;
                  return (s.nodeCount + s.imageRefCount * 2) > (best.nodeCount + best.imageRefCount * 2) ? s : best;
                }, null) : null;
                const warnRef = bestRecentSnap;
                const nodeDrop = warnRef ? currentNodeCount - warnRef.nodeCount : 0;
                const imgDrop = warnRef ? currentImageCount - warnRef.imageRefCount : 0;
                const showWarning = warnRef != null && !snapshotsLoading && (
                  (warnRef.nodeCount > 3 && nodeDrop < -Math.ceil(warnRef.nodeCount * 0.2)) ||
                  // Only warn about image loss if imageCount is reliably known (notebook not evicted)
                  (imageCountKnown && warnRef.imageRefCount > 2 && imgDrop < -Math.ceil(warnRef.imageRefCount * 0.2))
                );
                const diffSnap = snapshots.find(s => s.id === diffSnapshotId);
                const diffDateStr = diffSnap ? new Date(diffSnap.createdAt).toLocaleDateString(vi ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                      <Shield size={18} className="text-emerald-500" />
                      <span>{vi ? 'Sao lưu tự động' : 'Safety Snapshots'}</span>
                    </div>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl space-y-3">
                      <p className="text-xs text-emerald-900 dark:text-emerald-100 opacity-80 leading-relaxed">
                        {vi
                          ? 'Snapshot tự động được tạo trước các thao tác lớn để bảo vệ dữ liệu. Bạn có thể khôi phục bất kỳ lúc nào.'
                          : 'Snapshots are created automatically before large operations. Restore any version at any time.'}
                      </p>

                      {/* Warning banner — compare vs best recent snapshot (may differ from latest if auto-backup captured corrupted state) */}
                      {showWarning && warnRef && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                              {vi ? 'Phát hiện mất dữ liệu!' : 'Possible data loss detected!'}
                            </p>
                            <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5 leading-relaxed">
                              {vi
                                ? `Notebook hiện tại có${Math.abs(nodeDrop) > 0 ? ` ${Math.abs(nodeDrop)} ghi chú ít hơn` : ''}${Math.abs(imgDrop) > 0 ? ` và ${Math.abs(imgDrop)} ảnh ít hơn` : ''} so với bản backup tốt nhất gần đây (${warnRef.nodeCount}n · ${warnRef.imageRefCount}📸).`
                                : `Current has${Math.abs(nodeDrop) > 0 ? ` ${Math.abs(nodeDrop)} fewer note${Math.abs(nodeDrop) !== 1 ? 's' : ''}` : ''}${Math.abs(imgDrop) > 0 ? ` and ${Math.abs(imgDrop)} fewer screenshot${Math.abs(imgDrop) !== 1 ? 's' : ''}` : ''} than a recent good backup (${warnRef.nodeCount}n · ${warnRef.imageRefCount}📸).`}
                            </p>
                          </div>
                          <button
                            onClick={() => setDiffSnapshotId(warnRef.id)}
                            className="shrink-0 text-[10px] font-bold text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 whitespace-nowrap"
                          >
                            {vi ? 'Xem diff →' : 'Show diff →'}
                          </button>
                        </div>
                      )}

                      {/* Current state bar */}
                      <div className="flex items-center gap-3 text-[11px] bg-white/70 dark:bg-slate-800/70 rounded-lg px-3 py-2 border border-emerald-100 dark:border-emerald-900/30">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{vi ? 'Hiện tại:' : 'Current:'}</span>
                        <span className="text-gray-700 dark:text-gray-300">📝 {currentNodeCount} {vi ? 'ghi chú' : 'notes'}</span>
                        {currentImageCount > 0 && <span className="text-gray-700 dark:text-gray-300">📸 {currentImageCount}</span>}
                      </div>

                      {/* Notebook name + backup button — always backs up the active canvas notebook */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0 flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg bg-white/70 dark:bg-slate-800/70 border border-emerald-100 dark:border-emerald-900/30">
                          <span className="text-emerald-600 dark:text-emerald-400 shrink-0">📖</span>
                          <span className="text-gray-700 dark:text-gray-300 truncate font-medium">{curNb?.title ?? '—'}</span>
                        </div>
                        <button
                          onClick={async () => {
                            if (!onManualBackup || !snapshotNotebookId) return;
                            setSnapshotBacking(true);
                            const ok = await onManualBackup(snapshotNotebookId);
                            if (ok && onListSnapshots) {
                              const updated = await onListSnapshots(snapshotNotebookId);
                              setSnapshots(updated);
                            }
                            setSnapshotBacking(false);
                          }}
                          disabled={snapshotBacking || !snapshotNotebookId}
                          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
                        >
                          {snapshotBacking ? <Loader2 size={13} className="animate-spin" /> : <Shield size={13} />}
                          {vi ? 'Backup ngay' : 'Backup now'}
                        </button>
                      </div>

                      {/* Snapshot list */}
                      {snapshotsLoading ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 size={13} className="animate-spin" /> {vi ? 'Đang tải...' : 'Loading...'}
                        </div>
                      ) : snapshots.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-1">
                          {vi ? 'Chưa có snapshot nào.' : 'No snapshots yet.'}
                        </p>
                      ) : (
                        <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                          {snapshots.map(s => {
                            const isManual = s.reason === 'manual';
                            const reasonLabel = s.reason === 'manual'
                              ? (vi ? 'THỦ CÔNG' : 'MANUAL')
                              : s.reason === 'before_restore'
                              ? (vi ? 'TRƯỚC KHÔI PHỤC' : 'PRE-RESTORE')
                              : s.reason === 'before_pdf_upload'
                              ? (vi ? 'TRƯỚC IMPORT PDF' : 'PRE-PDF')
                              : s.reason === 'before_import'
                              ? (vi ? 'TRƯỚC IMPORT' : 'PRE-IMPORT')
                              : 'AUTO';
                            const d = new Date(s.createdAt);
                            const dateStr = d.toLocaleDateString(vi ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric' });
                            const timeStr = d.toLocaleTimeString(vi ? 'vi-VN' : 'en-US', { hour: '2-digit', minute: '2-digit' });
                            const isConfirming = restoreConfirm === s.id;
                            const isDiffOpen = diffSnapshotId === s.id;
                            const sNodeDelta = currentNodeCount - s.nodeCount;
                            const sImgDelta = imageCountKnown ? currentImageCount - s.imageRefCount : null;
                            const deltaIsLoss = sNodeDelta < -2 || (sImgDelta !== null && sImgDelta < -2);
                            const deltaIsGain = sNodeDelta >= 0 && (sImgDelta === null || sImgDelta >= 0);
                            return (
                              <div key={s.id} className="bg-white dark:bg-slate-800 rounded-lg px-3 py-2 text-xs space-y-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${isManual ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400'}`}>
                                    {reasonLabel}
                                  </span>
                                  <span className="text-gray-500 dark:text-gray-400 shrink-0">{dateStr} {timeStr}</span>
                                  <span className="text-gray-400 dark:text-gray-500 shrink-0">·</span>
                                  <span className="text-gray-600 dark:text-gray-300 shrink-0">{s.nodeCount}n</span>
                                  {s.imageRefCount > 0 && <span className="text-gray-400 dark:text-gray-500 shrink-0">· {s.imageRefCount}📸</span>}

                                  {/* Delta chip */}
                                  <span className={`shrink-0 font-mono text-[10px] px-1.5 py-0.5 rounded ${deltaIsLoss ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : deltaIsGain ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-500'}`}>
                                    {sNodeDelta >= 0 ? '+' : ''}{sNodeDelta}n{sImgDelta !== null ? ` ${sImgDelta >= 0 ? '+' : ''}${sImgDelta}📸` : ''}
                                  </span>

                                  <div className="ml-auto shrink-0 flex items-center gap-1">
                                    {/* Diff button */}
                                    {onGetCurrentNodes && (
                                      <button
                                        onClick={() => setDiffSnapshotId(isDiffOpen ? null : s.id)}
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${isDiffOpen ? 'bg-blue-600 text-white' : 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'}`}
                                      >
                                        <GitMerge size={10} />
                                        Diff
                                      </button>
                                    )}

                                    {/* Restore button / confirm */}
                                    {isConfirming ? (
                                      <div className="flex items-center gap-1">
                                        {(() => {
                                          const lostCount = currentNodeCount - s.nodeCount;
                                          return lostCount > 0 ? (
                                            <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                              ⚠️ {lostCount} {vi ? 'ghi chú sẽ mất' : 'notes lost'}
                                            </span>
                                          ) : null;
                                        })()}
                                        <button
                                          onClick={() => setRestoreConfirm(null)}
                                          className="px-2 py-0.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 text-[10px]"
                                        >
                                          {vi ? 'Hủy' : 'Cancel'}
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (!onRestoreSnapshot) return;
                                            setRestoring(true);
                                            setRestoreConfirm(null);
                                            await onRestoreSnapshot(snapshotNotebookId, s.id);
                                            if (onListSnapshots) {
                                              const updated = await onListSnapshots(snapshotNotebookId);
                                              setSnapshots(updated);
                                            }
                                            setRestoring(false);
                                          }}
                                          disabled={restoring}
                                          className="flex items-center gap-1 px-2 py-0.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] font-bold"
                                        >
                                          {restoring ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                                          {vi ? 'Xác nhận' : 'Confirm'}
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setRestoreConfirm(s.id)}
                                        disabled={restoring}
                                        className="flex items-center gap-1 px-2 py-0.5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded text-[10px] font-bold transition-colors disabled:opacity-50"
                                      >
                                        <RotateCcw size={10} />
                                        {vi ? 'Khôi phục' : 'Restore'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Diff panel is now a full-screen overlay — see below in return */}
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <HardDrive size={18} className="text-indigo-500" />
                  <span>{t.backupTitle}</span>
                </div>
                <div className="p-5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900/30 rounded-xl space-y-5">
                  <p className="text-xs text-indigo-900 dark:text-indigo-100 leading-relaxed opacity-80">
                    {t.backupDesc}
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={onExportBackup}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold transition-all shadow-md active:scale-[0.98]"
                    >
                      <Download size={18} />
                      {t.exportBtn}
                    </button>

                    <div className="h-px bg-indigo-200 dark:bg-indigo-800 my-1"></div>

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white dark:bg-slate-700 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300 rounded-xl text-sm font-bold transition-all hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:scale-[0.98]"
                    >
                      <Upload size={18} />
                      {t.importBtn}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".zip"
                      className="hidden"
                      onChange={handleFileChange}
                    />

                    <button
                      onClick={onExportSmmx}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white dark:bg-slate-700 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300 rounded-xl text-sm font-bold transition-all hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:scale-[0.98]"
                    >
                      <FileCode size={18} />
                      {t.exportSmmxBtn}
                    </button>

                    <button
                      onClick={() => smmxInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white dark:bg-slate-700 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300 rounded-xl text-sm font-bold transition-all hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:scale-[0.98]"
                    >
                      <FileCode size={18} />
                      {t.importSmmxBtn}
                    </button>
                    <input
                      ref={smmxInputRef}
                      type="file"
                      accept=".smmx,.zip"
                      className="hidden"
                      onChange={handleSmmxChange}
                    />
                  </div>
                </div>
              </div>

              {/* Semantic Search Index */}
              {onIndexAllNotes && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                    <span>🔍</span>
                    <span>Semantic Search Index</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {language === 'vietnamese'
                      ? 'Tạo vector embedding cho tất cả note để tìm kiếm ngữ nghĩa (⌘K). Chỉ cần chạy một lần — sau đó tự động cập nhật khi bạn chỉnh sửa note.'
                      : 'Generate embeddings for all existing notes to enable semantic search (⌘K). Only needed once — new edits are indexed automatically.'}
                  </p>
                  <IndexAllButton onIndexAllNotes={onIndexAllNotes} language={language} />
                </div>
              )}

              {/* Google Drive Backup */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <Cloud size={18} className="text-blue-500" />
                  <span>Google Drive Backup</span>
                </div>

                {!driveConnected ? (
                  <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 rounded-xl space-y-3">
                    <p className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed opacity-80">
                      Kết nối Google Drive để backup và khôi phục notebook trực tiếp — không cần tải file về máy. File được lưu trong thư mục <strong>StudyBoard Backups</strong>.
                    </p>
                    <button
                      onClick={onDriveConnect}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all shadow-md active:scale-[0.98]"
                    >
                      <Cloud size={18} />
                      Kết nối Google Drive
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                        <CheckCircle2 size={15} />
                        Đã kết nối Google Drive
                      </div>
                      <button onClick={onDriveDisconnect} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                        Ngắt kết nối
                      </button>
                    </div>

                    {/* Backup block */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 rounded-xl space-y-2">
                      <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase tracking-wide mb-3">☁ Sao lưu lên Drive</p>
                      {notebooks && notebooks.length > 0 ? notebooks.map(nb => {
                        const status = driveBackupStatus[nb.id] ?? 'idle';
                        return (
                          <div key={nb.id} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-800 border border-blue-100 dark:border-blue-900/30 rounded-lg px-3 py-2.5">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{nb.title}</p>
                              <p className="text-xs text-gray-400">{nb.nodeCount ?? 0} notes</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {status === 'success' && driveBackupUrls[nb.id] && (
                                <a
                                  href={driveBackupUrls[nb.id]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 text-green-700 dark:text-green-400 transition-colors border border-green-200 dark:border-green-800"
                                >
                                  <ExternalLink size={11} />
                                  {language === 'vietnamese' ? 'Mở Drive' : 'Open Drive'}
                                </a>
                              )}
                              <button
                                onClick={() => handleDriveBackup(nb.id)}
                                disabled={status === 'loading'}
                                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                                  status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                  status === 'error' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                  status === 'loading' ? 'bg-blue-100 text-blue-500 dark:bg-blue-900/30' :
                                  'bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 dark:text-blue-300'
                                }`}
                              >
                                {status === 'loading' ? <Loader2 size={12} className="animate-spin" /> :
                                 status === 'success' ? <CheckCircle2 size={12} /> :
                                 status === 'error' ? <AlertTriangle size={12} /> :
                                 <Cloud size={12} />}
                                {status === 'loading' ? (language === 'vietnamese' ? 'Đang lưu...' : 'Saving...') : status === 'success' ? (language === 'vietnamese' ? 'Đã lưu!' : 'Saved!') : status === 'error' ? (language === 'vietnamese' ? 'Thất bại' : 'Failed') : 'Backup'}
                              </button>
                            </div>
                          </div>
                        );
                      }) : <p className="text-xs text-blue-700 dark:text-blue-300 opacity-70">Không có notebook nào.</p>}
                    </div>

                    {/* Restore block */}
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl space-y-2">
                      <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 uppercase tracking-wide mb-3">↩ Khôi phục từ Drive</p>
                      {driveBackupRecords.length === 0 ? (
                        <p className="text-xs text-gray-400 py-1">Chưa có backup nào. Nhấn <strong>Backup</strong> ở trên để tạo bản sao lưu đầu tiên.</p>
                      ) : (
                        <div className="space-y-2">
                          {driveBackupRecords.map(f => {
                            const restoreStatus = driveRestoreStatus[f.id] ?? 'idle';
                            const date = new Date(f.modifiedTime).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                            const sizeMB = f.size ? (parseInt(f.size) / 1024 / 1024).toFixed(1) + ' MB' : '';
                            return (
                              <div key={f.id} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-800 border border-emerald-100 dark:border-emerald-900/30 rounded-lg px-3 py-2.5">
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{f.name}</p>
                                  <p className="text-xs text-gray-400">{date}{sizeMB ? ` · ${sizeMB}` : ''}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <a
                                    href={`https://drive.google.com/file/d/${f.id}/view`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={language === 'vietnamese' ? 'Xem trong Google Drive' : 'View in Google Drive'}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                                  >
                                    <ExternalLink size={13} />
                                  </a>
                                  <button
                                    onClick={() => handleDriveRestore(f.id)}
                                    disabled={restoreStatus === 'loading'}
                                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                                      restoreStatus === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                      restoreStatus === 'error' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                      restoreStatus === 'loading' ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30' :
                                      'bg-emerald-100 hover:bg-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:hover:bg-emerald-800/40 dark:text-emerald-300'
                                    }`}
                                  >
                                    {restoreStatus === 'loading' ? <Loader2 size={12} className="animate-spin" /> :
                                     restoreStatus === 'success' ? <CheckCircle2 size={12} /> :
                                     restoreStatus === 'error' ? <AlertTriangle size={12} /> :
                                     <Download size={12} />}
                                    {restoreStatus === 'loading' ? 'Đang khôi phục...' : restoreStatus === 'success' ? 'Đã khôi phục!' : restoreStatus === 'error' ? 'Thất bại' : 'Khôi phục'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* GITHUB PUBLISHING */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <Github size={18} className="text-gray-700 dark:text-gray-300" />
                  <span>{language === 'vietnamese' ? 'Publish lên GitHub' : 'GitHub Publishing'}</span>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/40 border border-gray-200 dark:border-slate-600 rounded-xl space-y-3">

                  {/* Not connected yet */}
                  {!githubStatus?.installationId ? (
                    <>
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        {language === 'vietnamese'
                          ? 'Kết nối GitHub để publish notebook thành Markdown repo đẹp. Không cần nhập token — chỉ cần nhấn nút bên dưới và chọn repo bạn muốn.'
                          : 'Connect GitHub to publish your notebooks as a clean Markdown repo. No token needed — just click below and choose a repo.'}
                      </p>
                      <button
                        onClick={onConnectGitHub}
                        className="w-full py-2.5 flex items-center justify-center gap-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 dark:hover:bg-white transition-colors"
                      >
                        <Github size={15} />
                        {language === 'vietnamese' ? 'Kết nối GitHub' : 'Connect GitHub'}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Connected — show account + repo picker */}
                      <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                        <CheckCircle2 size={13} />
                        <span>
                          {language === 'vietnamese' ? 'Đã kết nối' : 'Connected'}
                          {githubStatus.connectedAs ? ` @${githubStatus.connectedAs}` : ''}
                        </span>
                      </div>

                      {/* Repo picker */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                          Repository
                        </label>
                        {(githubStatus.availableRepos?.length ?? 0) > 0 ? (
                          <select
                            value={ghRepo}
                            onChange={e => setGhRepo(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg outline-none focus:border-blue-400 text-gray-800 dark:text-gray-200 font-mono"
                          >
                            <option value="">{language === 'vietnamese' ? '— Chọn repo —' : '— Select repo —'}</option>
                            {githubStatus.availableRepos!.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={ghRepo}
                            onChange={e => setGhRepo(e.target.value)}
                            placeholder="username/my-notes"
                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg outline-none focus:border-blue-400 text-gray-800 dark:text-gray-200 font-mono"
                          />
                        )}
                      </div>

                      {/* Branch */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Branch</label>
                        <input
                          type="text"
                          value={ghBranch}
                          onChange={e => setGhBranch(e.target.value)}
                          placeholder="main"
                          className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg outline-none focus:border-blue-400 text-gray-800 dark:text-gray-200 font-mono"
                        />
                      </div>

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={async () => {
                            if (!ghRepo || !onUpdateGitHubRepo) return;
                            setGhSaving(true);
                            try {
                              await onUpdateGitHubRepo(ghRepo, ghBranch || 'main');
                            } finally {
                              setGhSaving(false);
                            }
                          }}
                          disabled={!ghRepo || ghSaving}
                          className="flex-1 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-700 dark:hover:bg-white disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                        >
                          {ghSaving && <Loader2 size={13} className="animate-spin" />}
                          {language === 'vietnamese' ? 'Lưu' : 'Save'}
                        </button>
                        <button
                          onClick={async () => {
                            await onClearGitHubConfig?.();
                            setGhRepo('');
                            setGhBranch('main');
                          }}
                          className="px-4 py-2 text-sm font-medium border border-red-300 dark:border-red-700 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          {language === 'vietnamese' ? 'Ngắt kết nối' : 'Disconnect'}
                        </button>
                      </div>

                      {githubStatus.configured && githubStatus.repo && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 pt-1 border-t border-gray-200 dark:border-slate-600">
                          <CheckCircle2 size={11} className="text-green-500" />
                          {githubStatus.repo} / {githubStatus.branch}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* ADMIN TAB */}
          {activeTab === 'admin' && isAdmin && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-bold text-sm uppercase tracking-wider">
                  <ShieldCheck size={18} className="text-emerald-500" />
                  <span>Admin Panel</span>
                </div>
                <button onClick={fetchAdminStats} disabled={adminLoading} className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
                  <RefreshCw size={12} className={adminLoading ? 'animate-spin' : ''} /> Refresh
                </button>
              </div>

              {/* Simulate Free User toggle */}
              <button
                onClick={() => onSimulateFreeUserChange?.(!simulateFreeUser)}
                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                  simulateFreeUser ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700' : 'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <User size={16} className={simulateFreeUser ? 'text-orange-500' : 'text-gray-400'} />
                  <span className={`text-sm font-medium ${simulateFreeUser ? 'text-orange-700 dark:text-orange-300' : 'text-gray-700 dark:text-gray-200'}`}>Giả lập Free User</span>
                </div>
                <div className={`w-10 h-6 rounded-full p-1 transition-colors ${simulateFreeUser ? 'bg-orange-500' : 'bg-gray-300 dark:bg-slate-500'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${simulateFreeUser ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
              </button>

              {/* Stats cards */}
              {adminLoading && !adminStats && (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 size={20} className="animate-spin mr-2" /> Đang tải...
                </div>
              )}
              {adminError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-lg">{adminError}</div>
              )}
              {adminStats && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
                      <Users size={16} className="text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{adminStats.total}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Tổng users</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                      <Crown size={16} className="text-amber-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{adminStats.premiumCount}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Premium</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 text-center">
                      <BarChart2 size={16} className="text-green-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{adminStats.activeToday}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Active hôm nay</p>
                    </div>
                  </div>

                  {/* User list */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Danh sách users</p>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {adminStats.users.map(u => (
                        <button
                          key={u.uid}
                          onClick={() => fetchUserData(u)}
                          className="w-full flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-100 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left group"
                        >
                          {u.photoURL
                            ? <img src={u.photoURL} className="w-8 h-8 rounded-full flex-shrink-0" alt="" />
                            : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(u.name || u.email).charAt(0).toUpperCase()}</div>
                          }
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{u.name || '—'}</p>
                              {u.isPremium && <Crown size={10} className="text-amber-500 flex-shrink-0" fill="currentColor" />}
                              {u.todayUsage > 0 && <span className="text-[9px] bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 px-1.5 rounded-full font-bold">active</span>}
                            </div>
                            <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                          </div>
                          <div className="text-right flex-shrink-0 flex items-center gap-2">
                            <div>
                              <div className="flex items-center gap-1 text-[10px] text-gray-400 justify-end">
                                <BookOpen size={9} /> {u.notebookCount}
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5 justify-end">
                                <Clock size={9} />
                                {u.lastSeen ? new Date(u.lastSeen).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '—'}
                              </div>
                            </div>
                            <ChevronRight size={12} className="text-gray-300 group-hover:text-blue-400 transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-5 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-between items-center shrink-0">
          <button
            onClick={handleReload}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw size={14} /> {t.reload}
          </button>

          <button
            onClick={onClose}
            className="px-8 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
          >
            {t.close}
          </button>
        </div>

      </div>
    </div>

    {/* Diff full-screen overlay — no nested scroll, single scroll surface */}
    {diffSnapshotId && (() => {
      const vi = language === 'vietnamese';
      const diffSnap = snapshots.find(s => s.id === diffSnapshotId);
      const diffDateStr = diffSnap ? new Date(diffSnap.createdAt).toLocaleDateString(vi ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
      const diffReasonLabel = diffSnap?.reason === 'before_restore'
        ? (vi ? '(tự động trước khi khôi phục)' : '(auto before restore)')
        : diffSnap?.reason === 'before_pdf_upload'
        ? (vi ? '(tự động trước khi import PDF)' : '(auto before PDF import)')
        : diffSnap?.reason === 'before_import'
        ? (vi ? '(tự động trước khi import)' : '(auto before import)')
        : diffSnap?.reason === 'auto_guard'
        ? (vi ? '(tự động bảo vệ dữ liệu)' : '(auto data guard)')
        : null;
      const closeDiff = () => setDiffSnapshotId(null);
      const missingScreenshotCount = diffResult?.modified.filter(n => n.imageRemoved).length ?? 0;
      const trulyMissingNotes = diffResult?.deleted.filter(n => !n.likelyReAdded) ?? [];
      return (
        <div className="fixed inset-0 z-[10003] bg-white dark:bg-slate-900 flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shrink-0">
            <button onClick={closeDiff} className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors shrink-0">
              <ChevronRight size={15} className="rotate-180" />
              <span className="text-xs font-medium">{vi ? 'Quay lại' : 'Back'}</span>
            </button>
            <GitMerge size={13} className="text-blue-500 shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">
                {vi ? `So sánh với bản backup ${diffDateStr}` : `Diff vs backup ${diffDateStr}`}
              </span>
              {diffReasonLabel && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{diffReasonLabel}</span>
              )}
            </div>
            <button onClick={closeDiff} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          {diffLoading ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-sm text-gray-400">
              <Loader2 size={16} className="animate-spin" />
              {vi ? 'Đang phân tích...' : 'Analyzing diff...'}
            </div>
          ) : diffError ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
              <AlertTriangle size={28} className="text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{diffError}</p>
              <button onClick={() => setDiffKey(k => k + 1)} className="flex items-center gap-1 text-sm font-bold text-red-600 hover:underline">
                <RefreshCw size={13} /> {vi ? 'Thử lại' : 'Retry'}
              </button>
            </div>
          ) : diffResult ? (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Summary bar */}
              <div className="flex gap-4 px-4 py-2 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 text-xs flex-wrap shrink-0 items-center">
                {diffResult.added.length > 0 && <span className="text-green-600 dark:text-green-400 font-medium">+{diffResult.added.length} {vi ? 'thêm' : 'added'}</span>}
                {diffResult.deleted.length > 0 && <span className="text-red-500 dark:text-red-400 font-medium">−{diffResult.deleted.length} {vi ? 'xóa' : 'deleted'}</span>}
                {diffResult.modified.length > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">~{diffResult.modified.length} {vi ? 'sửa' : 'modified'}</span>}
                {diffResult.added.length === 0 && diffResult.deleted.length === 0 && diffResult.modified.length === 0 && (
                  <span className="text-gray-500 font-medium flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" />{vi ? 'Không có thay đổi' : 'No changes'}</span>
                )}
                <span className="text-gray-400 dark:text-gray-600 ml-auto">{diffResult.unchangedCount} {vi ? 'không đổi' : 'unchanged'}</span>
              </div>

              {/* Selective restore */}
              {(onRestoreMissingScreenshots || onRestoreMissingNotes) && (missingScreenshotCount > 0 || trulyMissingNotes.length > 0) && (
                <div className="px-4 py-2 bg-blue-50/60 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30 space-y-1.5 shrink-0">
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">{vi ? 'Khôi phục có chọn lọc:' : 'Selective restore:'}</p>
                  <div className="flex flex-wrap gap-2">
                    {missingScreenshotCount > 0 && onRestoreMissingScreenshots && (
                      <button disabled={selectiveRestoring !== null} onClick={async () => {
                        if (!diffSnapshotId) return;
                        setSelectiveRestoring('screenshots'); setSelectiveRestoreMsg(null);
                        try {
                          const { restored } = await onRestoreMissingScreenshots(snapshotNotebookId, diffSnapshotId);
                          setSelectiveRestoreMsg(vi ? `✓ Đã khôi phục ${restored} ảnh. Đang cập nhật diff...` : `✓ Restored ${restored} screenshot${restored !== 1 ? 's' : ''}. Rechecking...`);
                          setDiffKey(k => k + 1);
                        } catch { setSelectiveRestoreMsg(vi ? '⚠️ Lỗi khôi phục.' : '⚠️ Restore error.'); }
                        finally { setSelectiveRestoring(null); }
                      }} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[11px] font-bold transition-colors disabled:opacity-50">
                        {selectiveRestoring === 'screenshots' ? <Loader2 size={11} className="animate-spin" /> : null}
                        📸 {vi ? `Khôi phục ${missingScreenshotCount} ảnh bị mất` : `Restore ${missingScreenshotCount} missing screenshot${missingScreenshotCount !== 1 ? 's' : ''}`}
                      </button>
                    )}
                    {trulyMissingNotes.length > 0 && onRestoreMissingNotes && (
                      <button disabled={selectiveRestoring !== null} onClick={async () => {
                        if (!diffSnapshotId) return;
                        setSelectiveRestoring('notes'); setSelectiveRestoreMsg(null);
                        try {
                          const { restored } = await onRestoreMissingNotes(snapshotNotebookId, diffSnapshotId, trulyMissingNotes.map(n => n.id));
                          setSelectiveRestoreMsg(vi ? `✓ Đã thêm lại ${restored} ghi chú. Đang cập nhật diff...` : `✓ Re-added ${restored} note${restored !== 1 ? 's' : ''}. Rechecking...`);
                          setDiffKey(k => k + 1);
                        } catch { setSelectiveRestoreMsg(vi ? '⚠️ Lỗi khôi phục.' : '⚠️ Restore error.'); }
                        finally { setSelectiveRestoring(null); }
                      }} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-bold transition-colors disabled:opacity-50">
                        {selectiveRestoring === 'notes' ? <Loader2 size={11} className="animate-spin" /> : null}
                        📝 {vi ? `Khôi phục ${trulyMissingNotes.length} ghi chú bị mất` : `Restore ${trulyMissingNotes.length} missing note${trulyMissingNotes.length !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                  {selectiveRestoreMsg && <p className="text-[11px] text-blue-700 dark:text-blue-300">{selectiveRestoreMsg}</p>}
                </div>
              )}

              {/* Main split: node list (left) + AI panel (right) */}
              <div className="flex-1 flex min-h-0">

              {/* Node list — single scroll, no nested max-h */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800">
                {diffResult.unchangedCount > 0 && diffResult.added.length === 0 && diffResult.deleted.length === 0 && diffResult.modified.length === 0 && (
                  <div className="px-4 py-8 text-sm text-gray-400 italic text-center">{vi ? 'Tất cả ghi chú giống nhau.' : 'All notes are identical.'}</div>
                )}
                {diffResult.added.map(n => (
                  <div key={n.id} className="flex items-center gap-2 px-4 py-2.5 bg-green-50/60 dark:bg-green-900/10">
                    <FilePlus size={13} className="text-green-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-green-700 dark:text-green-400 font-medium block truncate">{n.title || '(Untitled)'}</span>
                      {n.createdAt && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                          {new Date(n.createdAt).toLocaleDateString(vi ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {n.titleSource === 'ai' && <span className="ml-1 text-violet-400">· AI</span>}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-green-500 shrink-0">{vi ? 'Mới' : 'New'}</span>
                    {onOpenNote && snapshotNotebookId && (
                      <button onClick={() => { onOpenNote(snapshotNotebookId, n.id); }} title={vi ? 'Mở note này' : 'Open this note'} className="shrink-0 p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors">
                        <ExternalLink size={12} className="text-green-500" />
                      </button>
                    )}
                  </div>
                ))}
                {diffResult.deleted.map(n => n.likelyReAdded ? (
                  <div key={n.id}>
                    <button onClick={() => setDiffExpandedId(diffExpandedId === n.id ? null : n.id)} className="w-full flex items-center gap-2 px-4 py-2.5 bg-amber-50/40 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-left transition-colors">
                      <Trash2 size={13} className="text-amber-400 shrink-0" />
                      <span className="text-xs text-amber-700 dark:text-amber-400 flex-1 min-w-0 truncate">{n.title || '(Untitled)'}</span>
                      <span className="text-[10px] text-amber-500 shrink-0">{vi ? 'Có thể đã tạo lại' : 'Likely re-created'}</span>
                      {diffExpandedId === n.id ? <ChevronDown size={11} className="text-amber-400 shrink-0" /> : <ChevronRight size={11} className="text-gray-400 shrink-0" />}
                    </button>
                    {diffExpandedId === n.id && (
                      <div className="px-4 py-2 bg-amber-50/30 dark:bg-amber-900/5 text-[10px] flex gap-3 border-t border-amber-100 dark:border-amber-900/20">
                        <span className="text-red-500">— {vi ? 'bản backup' : 'backup'}</span>
                        <span className="text-green-600">+ {vi ? 'bản hiện tại' : 'current'}</span>
                      </div>
                    )}
                    {diffExpandedId === n.id && <SnapDiffViewer lines={computeSnapLineDiff(n.snapContent, n.currentContent)} />}
                  </div>
                ) : (
                  <div key={n.id} className="flex items-center gap-2 px-4 py-2.5 bg-red-50/60 dark:bg-red-900/10">
                    <Trash2 size={13} className="text-red-400 shrink-0" />
                    <span className="text-xs text-red-600 dark:text-red-400 font-medium flex-1 min-w-0 truncate">{n.title || '(Untitled)'}</span>
                    <span className="text-[10px] text-red-400 shrink-0">{vi ? 'Thật sự mất' : 'Truly missing'}</span>
                  </div>
                ))}
                {diffResult.modified.map(n => {
                  const isExp = diffExpandedId === n.id;
                  return (
                    <div key={n.id}>
                      <div className="flex items-center bg-amber-50/60 dark:bg-amber-900/10">
                        <button onClick={() => setDiffExpandedId(isExp ? null : n.id)} className="flex-1 flex items-center gap-2 px-4 py-2.5 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-left transition-colors min-w-0">
                          <FileEdit size={13} className="text-amber-500 shrink-0" />
                          <span className="text-xs text-amber-700 dark:text-amber-400 font-medium flex-1 min-w-0 truncate">{n.title}</span>
                          {n.imageAdded && <span className="text-[10px] text-green-500 shrink-0">📸+</span>}
                          {n.imageRemoved && <span className="text-[10px] text-red-400 shrink-0">📸−</span>}
                          {n.imageChanged && <span className="text-[10px] text-amber-500 shrink-0">📸↺</span>}
                          <span className="text-[10px] text-amber-500 shrink-0">{vi ? 'Sửa' : 'Modified'}</span>
                          {isExp ? <ChevronDown size={11} className="text-amber-500 shrink-0" /> : <ChevronRight size={11} className="text-gray-400 shrink-0" />}
                        </button>
                        {onOpenNote && snapshotNotebookId && (
                          <button onClick={() => { onOpenNote(snapshotNotebookId, n.id); }} title={vi ? 'Mở note này' : 'Open this note'} className="shrink-0 px-3 py-2.5 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors border-l border-amber-100 dark:border-amber-900/30">
                            <ExternalLink size={12} className="text-amber-500" />
                          </button>
                        )}
                      </div>
                      {isExp && (
                        <div className="border-t border-amber-100 dark:border-amber-900/30">
                          {n.titleChanged && (
                            <div className="px-4 py-2.5 grid grid-cols-2 gap-3 border-b border-amber-100 dark:border-amber-900/30 bg-amber-50/30 dark:bg-amber-900/10">
                              <div>
                                <div className="text-[9px] font-semibold text-red-400 uppercase tracking-wide mb-0.5">{vi ? 'Tiêu đề cũ' : 'Old title'}</div>
                                <div className="text-xs text-red-500 line-through opacity-80">{n.oldTitle || '(Untitled)'}</div>
                              </div>
                              <div>
                                <div className="text-[9px] font-semibold text-green-600 uppercase tracking-wide mb-0.5">{vi ? 'Tiêu đề mới' : 'New title'}</div>
                                <div className="text-xs text-green-700 dark:text-green-400 font-medium">{n.newTitle || '(Untitled)'}</div>
                              </div>
                            </div>
                          )}
                          {(n.contentChanged || n.imageAdded || n.imageRemoved || n.imageChanged) && (
                            <div className="grid grid-cols-2 divide-x divide-amber-100 dark:divide-amber-900/30">
                              <div className="p-4 space-y-3 bg-red-50/20 dark:bg-red-900/5">
                                <div className="text-[9px] font-semibold text-red-400 uppercase tracking-wide">
                                  ◀ {vi ? 'Bản backup' : 'Backup version'}
                                  {n.imageChanged && <span className="ml-2 text-amber-500">{vi ? '(ảnh đã thay)' : '(image replaced)'}</span>}
                                </div>
                                {n.snapImageBase64 && <img src={n.snapImageBase64} alt="backup" className="w-full rounded object-contain border border-red-100 dark:border-red-900/30 bg-white dark:bg-slate-900" style={{ maxHeight: '50vh' }} />}
                                {!n.snapImageBase64 && n.imageAdded && <div className="text-[10px] text-gray-300 dark:text-slate-600 italic">{vi ? '(chưa có ảnh)' : '(no image)'}</div>}
                                {n.contentChanged && (
                                  <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none [&>h1]:text-base [&>h1]:font-semibold [&>h2]:text-sm [&>h2]:font-semibold [&>p]:my-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4"
                                    dangerouslySetInnerHTML={{ __html: _md.render(normalizeForDisplay(n.snapRawContent)) }} />
                                )}
                              </div>
                              <div className="p-4 space-y-3 bg-green-50/20 dark:bg-green-900/5">
                                <div className="text-[9px] font-semibold text-green-600 uppercase tracking-wide">
                                  ▶ {vi ? 'Bản hiện tại' : 'Current version'}
                                  {(n.imageChanged || (n.imageAdded && n.curAllImages.length > 0)) && <span className="ml-2 text-green-600">{vi ? `(${n.curAllImages.length} ảnh mới)` : `(${n.curAllImages.length} new image${n.curAllImages.length > 1 ? 's' : ''})`}</span>}
                                </div>
                                {n.curAllImages.map((src, i) => (
                                  <img key={i} src={src} alt={`current-${i + 1}`} className="w-full rounded object-contain border border-green-100 dark:border-green-900/30 bg-white dark:bg-slate-900" style={{ maxHeight: '50vh' }} />
                                ))}
                                {n.curAllImages.length === 0 && n.imageRemoved && <div className="text-[10px] text-gray-400 dark:text-slate-500 italic">{vi ? '(ảnh đã bị xóa)' : '(image removed)'}</div>}
                                {n.contentChanged && (
                                  <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none [&>h1]:text-base [&>h1]:font-semibold [&>h2]:text-sm [&>h2]:font-semibold [&>p]:my-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4"
                                    dangerouslySetInnerHTML={{ __html: _md.render(normalizeForDisplay(n.curRawContent)) }} />
                                )}
                              </div>
                            </div>
                          )}
                          {n.contentChanged && (
                            <details className="border-t border-amber-100 dark:border-amber-900/30">
                              <summary className="px-4 py-2 text-[10px] text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-slate-300 select-none">
                                {vi ? '↔ Xem thay đổi theo dòng' : '↔ Line-by-line diff'}
                              </summary>
                              <SnapDiffViewer lines={computeSnapLineDiff(n.oldContent, n.newContent)} />
                            </details>
                          )}
                          {onRevertNodeChanges && (n.titleChanged || n.contentChanged || n.imageRemoved) && snapshotNotebookId && (
                            <div className="flex flex-wrap gap-1.5 px-4 py-2.5 border-t border-amber-100 dark:border-amber-900/30 bg-amber-50/20 dark:bg-amber-900/5">
                              {n.titleChanged && (
                                <button onClick={async () => { await onRevertNodeChanges(snapshotNotebookId, n.id, { title: n.snapRawTitle }); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors">
                                  <RotateCcw size={9} />{vi ? 'Khôi phục tiêu đề' : 'Revert title'}
                                </button>
                              )}
                              {n.contentChanged && (
                                <button onClick={async () => { await onRevertNodeChanges(snapshotNotebookId, n.id, { content: n.snapRawContent }); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors">
                                  <RotateCcw size={9} />{vi ? 'Khôi phục nội dung' : 'Revert content'}
                                </button>
                              )}
                              {n.imageRemoved && n.snapImageBase64 && (
                                <button onClick={async () => { await onRevertNodeChanges(snapshotNotebookId, n.id, { imageBase64: n.snapImageBase64 }); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors">
                                  <RotateCcw size={9} />{vi ? 'Khôi phục ảnh' : 'Revert image'}
                                </button>
                              )}
                              {n.titleChanged && n.contentChanged && (
                                <button onClick={async () => { await onRevertNodeChanges(snapshotNotebookId, n.id, { title: n.snapRawTitle, content: n.snapRawContent }); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors">
                                  <RotateCcw size={9} />{vi ? 'Khôi phục tất cả' : 'Revert all'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Drag separator — only when AI panel open */}
              {showSnapAiChat && (
                <div
                  onMouseDown={handleAiPanelDragStart}
                  className="w-1 shrink-0 cursor-col-resize bg-gray-200 dark:bg-slate-700 hover:bg-indigo-400 dark:hover:bg-indigo-500 transition-colors select-none"
                />
              )}

              {/* Right: AI Panel */}
              {showSnapAiChat ? (
                <div className="flex flex-col border-l border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shrink-0" style={{ width: aiPanelWidth }}>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-950/40 border-b border-indigo-100 dark:border-indigo-900/30 shrink-0">
                    <Bot size={13} className="text-indigo-500 shrink-0" />
                    <span className="flex-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">{vi ? 'AI Giải thích thay đổi' : 'AI Diff Assistant'}</span>
                    <button onClick={() => setShowSnapAiChat(false)} className="text-indigo-300 hover:text-indigo-500 transition-colors"><X size={13} /></button>
                  </div>
                  {snapAiChat.length === 0 && (
                    <div className="flex flex-col gap-2 px-4 py-3">
                      {[vi ? 'Tao có mất nội dung không?' : 'Did I lose any content?', vi ? 'Tóm tắt thay đổi cho tao' : 'Summarize what changed', vi ? 'Tại sao có nhiều thay đổi vậy?' : 'Why so many changes?'].map(q => (
                        <button key={q} onClick={() => sendSnapAiMessage(q)} disabled={snapAiLoading} className="text-[11px] px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50 text-left">{q}</button>
                      ))}
                    </div>
                  )}
                  {snapAiChat.length > 0 && (
                    <div ref={snapAiScrollRef} className="flex-1 flex flex-col gap-2 overflow-y-auto px-4 py-3">
                      {snapAiChat.map((m, i) => (
                        <div key={i} className={`flex gap-1.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          {m.role === 'model' && <Bot size={12} className="text-indigo-400 shrink-0 mt-0.5" />}
                          <div className={`text-[11px] leading-relaxed rounded-lg px-2.5 py-1.5 max-w-[90%] whitespace-pre-wrap ${m.role === 'user' ? 'bg-indigo-500 text-white rounded-br-sm' : 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-gray-200 rounded-bl-sm'}`}>{m.text}</div>
                        </div>
                      ))}
                      {snapAiLoading && (
                        <div className="flex gap-1.5 items-center">
                          <Bot size={12} className="text-indigo-400 shrink-0" />
                          <div className="flex gap-1 px-2.5 py-1.5 bg-gray-100 dark:bg-slate-700 rounded-lg rounded-bl-sm">
                            <span className="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1.5 px-4 py-2.5 border-t border-gray-100 dark:border-slate-800 shrink-0 items-center">
                    <button
                      onClick={toggleSnapAiMic}
                      disabled={snapAiLoading}
                      title={snapAiMicActive ? (vi ? 'Dừng ghi âm' : 'Stop recording') : (vi ? 'Nói vào micro' : 'Speak')}
                      className={`p-1.5 rounded-lg transition-colors shrink-0 disabled:opacity-40 ${snapAiMicActive ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : 'text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'}`}
                    >
                      {snapAiMicActive ? <MicOff size={13} /> : <Mic size={13} />}
                    </button>
                    <input type="text" value={snapAiInput} onChange={e => setSnapAiInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendSnapAiMessage(snapAiInput); setSnapAiInput(''); } }}
                      placeholder={snapAiMicActive ? (vi ? 'Đang nghe...' : 'Listening...') : (vi ? 'Hỏi về thay đổi...' : 'Ask about the changes...')}
                      disabled={snapAiLoading}
                      className="flex-1 text-[11px] px-2 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:opacity-50" />
                    <button onClick={() => { sendSnapAiMessage(snapAiInput); setSnapAiInput(''); }} disabled={snapAiLoading || !snapAiInput.trim()} className="p-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-40 shrink-0">
                      <Send size={12} />
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowSnapAiChat(true)} title={vi ? 'Hỏi AI về thay đổi này' : 'Ask AI about these changes'} className="w-9 flex flex-col items-center justify-center gap-2 border-l border-gray-200 dark:border-slate-700 text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors shrink-0">
                  <Bot size={14} />
                  <span className="text-[8px] font-bold uppercase tracking-wider text-indigo-400" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>AI</span>
                </button>
              )}

              </div>
            </div>
          ) : null}
        </div>
      );
    })()}

    {/* Admin user viewer — full-screen overlay */}
    {viewingUser && !viewingUserLoading && (
      <AdminUserViewer
        user={{ uid: viewingUser.uid, name: viewingUser.name, email: viewingUser.email, photoURL: viewingUser.photoURL, isPremium: viewingUser.isPremium }}
        notebooks={viewingUserNotebooks}
        onClose={() => { setViewingUser(null); setViewingUserNotebooks([]); }}
      />
    )}
    {viewingUserLoading && (
      <div className="fixed inset-0 z-[10002] bg-black/40 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 flex items-center gap-3 shadow-2xl">
          <Loader2 size={20} className="animate-spin text-blue-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Đang tải dữ liệu...</span>
        </div>
      </div>
    )}
    </>
  );
};
